import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

const NavigationGuardContext = createContext(null);

// Keep the editor reusable in dialogs while the app owns browser history.
export function NavigationGuardProvider({ children }) {
  const guardRef = useRef(null);
  const shouldBlock = useCallback(() => Boolean(guardRef.current?.()), []);
  const blocker = useBlocker(shouldBlock);
  const register = useCallback((guard) => {
    guardRef.current = guard;
    return () => { if (guardRef.current === guard) guardRef.current = null; };
  }, []);
  const value = useMemo(() => ({ blocker, register }), [blocker, register]);
  return <NavigationGuardContext.Provider value={value}>{children}</NavigationGuardContext.Provider>;
}

export function useNavigationGuard(dirty) {
  const context = useContext(NavigationGuardContext);
  const register = context?.register;
  const blocker = context?.blocker;
  const allowed = useRef(false);
  useLayoutEffect(() => register?.(() => dirty && !allowed.current), [register, dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      if (allowed.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return {
    blocked: blocker?.state === 'blocked',
    stay: () => { if (blocker?.state === 'blocked') blocker.reset(); },
    leave: (fallback) => {
      allowed.current = true;
      if (blocker?.state === 'blocked') blocker.proceed();
      else fallback?.();
    },
    saved: (onSaved) => {
      allowed.current = true;
      if (blocker?.state === 'blocked') blocker.reset();
      onSaved?.();
    },
  };
}
